"use client";

import Link from "next/link";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { isRTL, type Locale, localePath } from "@/lib/i18n";
import { useSwitcherLocales } from "@/lib/useSwitcherLocales";
import { setManualLocale } from "@/lib/localePreference";
import {
    Award,
    Mail,
    Globe,
} from "lucide-react";

type FooterProps = {
    locale: Locale;
};

export default function Footer({ locale }: FooterProps) {
    const tFooter = useTranslations("footer");
    const tNav = useTranslations("nav");
    const tGeneral = useTranslations("general");
    const tBrand = useTranslations("brand");
    const tHeader = useTranslations("header");
    const tLang = useTranslations("lang");
    const tCerts = useTranslations("certs");
    const isRtl = isRTL(locale);
    const switcherLocales = useSwitcherLocales();

    const flagFor = (code: Locale): string => {
        switch (code) {
            case "fa":
                return "/assets/ir.png";
            case "ar":
                return "/assets/ar.png";
            case "en":
                return "/assets/us.png";
            case "de":
                return "/assets/de.png";
            case "tr":
                return "/assets/tr.png";
            default:
                return "/assets/us.png";
        }
    };

    const nativeLangNames: Record<Locale, string> = {
        en: "English",
        ar: "العربية",
        de: "Deutsch",
        tr: "Türkçe",
        fa: "فارسی",
    };

    const platformLinks = [
        { label: tNav("submenu.platform.tracking"), href: localePath(locale, "platform/tracking") },
        { label: tNav("submenu.platform.safety"), href: localePath(locale, "platform/safety") },
        { label: tNav("submenu.platform.fleetOps"), href: localePath(locale, "platform/fleet-ops") },
        { label: tNav("platform"), href: localePath(locale, "platform") },
        { label: tNav("whiteLabel"), href: localePath(locale, "white-label") },
    ];

    const solutionLinks = [
        { label: tNav("submenu.solutions.logistics"), href: localePath(locale, "solutions/logistics") },
        { label: tNav("submenu.solutions.government"), href: localePath(locale, "solutions/government") },
        { label: tNav("submenu.solutions.passengerTransport"), href: localePath(locale, "solutions/passenger-transport") },
        { label: tNav("submenu.solutions.fieldService"), href: localePath(locale, "solutions/field-service") },
        { label: tNav("solutions"), href: localePath(locale, "solutions") },
        { label: tNav("customDevelopment"), href: localePath(locale, "custom-development") },
    ];

    const companyLinks = [
        { label: tNav("about"), href: localePath(locale, "about") },
        { label: tGeneral("knowledge-base.title") || "Knowledge Base", href: localePath(locale, "knowledge-base") },
        { label: tNav("contact"), href: localePath(locale, "contactus") },
    ];

    const certifications = [
        "ISO 9001:2015",
        "ISO 27001",
        "CE Compliant",
        "FCC Approved",
    ];

    return (
        <footer className="bg-slate-950 text-slate-400 border-t border-slate-900 mt-20 pt-16 pb-12" dir={isRtl ? "rtl" : "ltr"}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Main Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 mb-14">
                    {/* Brand / About Column */}
                    <div className={`lg:col-span-4 ${isRtl ? "text-right" : "text-left"}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center p-1.5 shadow-sm">
                                <Image
                                    src="/logo.png"
                                    alt="Farjad System Pardaz"
                                    width={36}
                                    height={36}
                                    className="object-contain brightness-110"
                                />
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-base tracking-tight">
                                    {locale === "ar" ? tBrand("name") : "Farjad System Pardaz"}
                                </h3>
                                <p className="text-xs text-brand-orange font-medium">
                                    {tHeader("subtitle") || "Fleet Tracking & Telematics"}
                                </p>
                            </div>
                        </div>

                        <p className="text-sm text-slate-400 leading-relaxed mb-6 max-w-sm">
                            {locale === "ar"
                                ? "برمجيات متقدمة لإدارة وتتبع الأساطيل، حلول العلامة البيضاء، وتطوير الأنظمة المخصصة لتلبية أعلى معايير التتبع الدولية."
                                : locale === "de"
                                ? "Fortschrittliche Software für GPS-Flottenortung, Telematik, White-Label-Plattformen und maßgeschneiderte Unternehmenslösungen."
                                : locale === "tr"
                                ? "Gelişmiş GPS filo takip ve telematik yazılımları, beyaz etiket çözümleri ve kurumsal özel geliştirme platformu."
                                : "Enterprise GPS fleet management, telematics and IoT software solutions. Built for reliability, scalability and full operational visibility."}
                        </p>

                        {/* Certification Badges */}
                        <div className="mb-6">
                            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                                <Award size={14} className="text-brand-orange" />
                                <span>{tCerts("title") || "Compliance & Standards"}</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {certifications.map((cert) => (
                                    <span
                                        key={cert}
                                        className="text-[11px] font-medium bg-slate-900 text-slate-300 border border-slate-800/80 px-2.5 py-1 rounded-md"
                                    >
                                        {cert}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Social Links */}
                        <div className="flex items-center gap-3">
                            <a
                                href="https://www.linkedin.com/company/farjad-system-pardaz-"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-9 h-9 rounded-lg bg-slate-900 hover:bg-brand-orange border border-slate-800 hover:border-brand-orange transition-all flex items-center justify-center text-slate-300 hover:text-white group"
                                title="LinkedIn"
                                aria-label="LinkedIn"
                            >
                                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9h2.79v8.37H6.46v-8.37M7.86 6.64a1.62 1.62 0 1 0 0 3.24 1.62 1.62 0 0 0 0-3.24z" />
                                </svg>
                            </a>
                            <a
                                href="https://www.facebook.com/share/1DNbxrknze/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-9 h-9 rounded-lg bg-slate-900 hover:bg-brand-orange border border-slate-800 hover:border-brand-orange transition-all flex items-center justify-center text-slate-300 hover:text-white group"
                                title="Facebook"
                                aria-label="Facebook"
                            >
                                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                    <path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02z" />
                                </svg>
                            </a>
                            <Link
                                href={localePath(locale, "contactus")}
                                className="w-9 h-9 rounded-lg bg-slate-900 hover:bg-brand-orange border border-slate-800 hover:border-brand-orange transition-all flex items-center justify-center text-slate-300 hover:text-white"
                                title="Contact"
                                aria-label="Contact"
                            >
                                <Mail size={16} />
                            </Link>
                        </div>
                    </div>

                    {/* Platform Links Column */}
                    <div className={`lg:col-span-3 ${isRtl ? "text-right" : "text-left"}`}>
                        <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                            {tFooter("platform")}
                        </h4>
                        <ul className="space-y-2.5">
                            {platformLinks.map((item) => (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        className="text-sm text-slate-400 hover:text-brand-orange transition-colors inline-flex items-center gap-1 group"
                                    >
                                        <span className="group-hover:translate-x-0.5 transition-transform">
                                            {item.label}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Solutions Column */}
                    <div className={`lg:col-span-3 ${isRtl ? "text-right" : "text-left"}`}>
                        <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                            {tFooter("solutions")}
                        </h4>
                        <ul className="space-y-2.5">
                            {solutionLinks.map((item) => (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        className="text-sm text-slate-400 hover:text-brand-orange transition-colors inline-flex items-center gap-1 group"
                                    >
                                        <span className="group-hover:translate-x-0.5 transition-transform">
                                            {item.label}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Company & Resources Column */}
                    <div className={`lg:col-span-2 ${isRtl ? "text-right" : "text-left"}`}>
                        <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                            {tFooter("company")}
                        </h4>
                        <ul className="space-y-2.5 mb-6">
                            {companyLinks.map((item) => (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        className="text-sm text-slate-400 hover:text-brand-orange transition-colors inline-flex items-center gap-1 group"
                                    >
                                        <span className="group-hover:translate-x-0.5 transition-transform">
                                            {item.label}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        {/* Quick CTA Box */}
                        <div className="p-3.5 rounded-xl bg-slate-900/80 border border-slate-800">
                            <div className="text-xs font-semibold text-white mb-1">
                                {locale === "ar" ? "جاهز للبدء؟" : "Ready to upgrade?"}
                            </div>
                            <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                                {locale === "ar"
                                    ? "تواصل مع فريقنا لاستشارة حلول الأسطول."
                                    : "Get a live demo tailored to your operations."}
                            </p>
                            <Link
                                href={localePath(locale, "contactus")}
                                className="block w-full rounded-lg px-3 py-1.5 text-center text-xs font-semibold text-white shadow-[0_8px_20px_rgba(13,148,136,.3)] transition hover:-translate-y-0.5"
                                style={{background: 'linear-gradient(140deg, var(--sm-teal-400), var(--sm-teal) 60%)'}}
                            >
                                {tNav("contact")}
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar Divider */}
                <div className="border-t border-slate-800/80 pt-8 mt-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
                        {/* Copyright */}
                        <div>
                            <p>{tFooter("copyright")}</p>
                        </div>

                        {/* Language switcher pills */}
                        <div className="flex items-center flex-wrap gap-2">
                            <span className="text-slate-600 flex items-center gap-1">
                                <Globe size={13} />
                            </span>
                            {switcherLocales.map((code) => {
                                const isCurrent = code === locale;
                                return (
                                    <Link
                                        key={code}
                                        href={localePath(code)}
                                        onClick={() => setManualLocale(code)}
                                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] transition-colors ${
                                            isCurrent
                                                ? "bg-slate-800 text-brand-orange font-semibold"
                                                : "text-slate-400 hover:text-white hover:bg-slate-900"
                                        }`}
                                    >
                                        <span className="relative w-3.5 h-2.5 overflow-hidden rounded-xs shrink-0">
                                            <Image
                                                src={flagFor(code)}
                                                alt={tLang(code)}
                                                fill
                                                sizes="14px"
                                                className="object-cover"
                                            />
                                        </span>
                                        <span>{nativeLangNames[code] || tLang(code)}</span>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
